import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import DownloadIcon from '@grnhse/seedling/lib/birch/icons/download';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import DocumentIcon from 'shared/components/documents/document_icon';
import { withLocalePrefix } from 'shared/utils/translation';
import { Document } from 'talent_matching/types';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';

const t = withLocalePrefix('talent_matching');

const CardContainer = styled.div<{ $clickable: boolean; $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${FullPalette.EVERGREEN_E100};
  border-radius: 4px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background-color: ${({ $selected }) =>
    $selected ? Tokens.colors.selected : 'transparent'};

  &:hover {
    ${({ $clickable }) =>
      $clickable && `background-color: ${Tokens.colors.hover};`}
  }
`;

const TruncatedBody = styled(Body)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

export type DocumentCardProps = {
  document: Document;
  onClick?: () => void;
  selected?: boolean;
};

const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  onClick,
  selected = false,
}) => {
  const { canDownloadCandidateData } = useTalentMatchingTabContext();
  const fileName = document.display_name || document.file_name;
  const isClickable = Boolean(onClick);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (document.downloadUrl) {
      window.open(document.downloadUrl, '_blank');
    }
  };

  return (
    <CardContainer
      $clickable={isClickable}
      $selected={selected}
      onClick={onClick}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ minWidth: 0, flex: '1 1 auto' }}
      >
        <DocumentIcon size="md" extension={document.extension} />
        <TruncatedBody title={fileName}>{fileName}</TruncatedBody>
      </Stack>
      {canDownloadCandidateData && document.downloadUrl && (
        <IconButton
          icon={DownloadIcon}
          label={t('actions.download')}
          onClick={handleDownload}
          size="sm"
        />
      )}
    </CardContainer>
  );
};

export default DocumentCard;
